import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "customerType", "firstName", "lastName", "middleName", "email", "businessName", "companyName", "contact", "dateOfBirth", "externalCustomerId", "buyerTypeNum", "suffix", "salutation", "displayId", "language"],
    _excluded2 = ["email"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _first from 'lodash/first';
import _find from 'lodash/find';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _head from 'lodash/head';
import _size from 'lodash/size';
import _castArray from 'lodash/castArray';
import _compact from 'lodash/compact';
import _isNil from 'lodash/isNil';
import _concat from 'lodash/concat';
import _property from 'lodash/property';
import { defaultMemoize } from 'reselect';
import { toMoment, DATE_TIME_FORMATS, getFormattedDateTime } from 'tbase/utils/dateUtils';
import { NO_DATA, EMPTY_ARRAY, EMPTY_STRING, EMPTY_OBJECT } from 'tbase/app.constants';
import { ADDRESS_TYPE as ADDRESS_TYPES } from 'tbase/constants/address.constants';
import { capitalizeFirstLetters } from 'tbase/formatters/string';
import { CUSTOMER_TYPE_DISPLAY } from 'tbase/constants/customer.constants';
import { CUSTOMER_ADDRESS_TYPES, BUYER_TYPE } from '../constants/deal.constants';
import { DSE_CONTACT_TYPES } from '../constants/constants';
import { CUSTOMER_TYPE, EMPLOYER_TYPE, ADDRESS_TYPE, DURATION_TYPES } from '../constants/customerForm.constants'; // ==========================================================================================================
// Readers related to Customers from Customer Management System (CMS)
// ==========================================================================================================

export var getFirstName = _property('firstName') || EMPTY_STRING;

var getBusinessCustomerName = function getBusinessCustomerName(customer) {
  return capitalizeFirstLetters(_get(customer, 'legalName') || _get(customer, 'tradeName') || _get(customer, 'companyName'));
};

var getBuyerType = function getBuyerType(customer) {
  return _get(customer, 'buyerTypeNum') || _get(customer, 'customerType');
};

export var getCustomerFirstName = function getCustomerFirstName(customer) {
  if (getBuyerType(customer) === CUSTOMER_TYPE.BUSINESS) return getBusinessCustomerName(customer);
  return getFirstName(customer);
};
export var getCustomerDisplayName = function getCustomerDisplayName(customer) {
  if (getBuyerType(customer) === CUSTOMER_TYPE.BUSINESS) return getBusinessCustomerName(customer);

  var _ref = customer || {},
      firstName = _ref.firstName,
      lastName = _ref.lastName,
      middleName = _ref.middleName,
      displayName = _ref.displayName;

  if (firstName || middleName || lastName) {
    return capitalizeFirstLetters([firstName, middleName, lastName].filter(Boolean).join(' '));
  }

  return capitalizeFirstLetters(displayName || EMPTY_STRING);
};
export var getDisplayName = function getDisplayName(customer) {
  var _ref2 = customer || {},
      firstName = _ref2.firstName,
      lastName = _ref2.lastName,
      companyName = _ref2.companyName;

  if (!_isEmpty(firstName) && !_isEmpty(lastName)) return "".concat(firstName, " ").concat(lastName);
  if (_isEmpty(lastName) && !_isEmpty(firstName)) return "".concat(firstName);
  if (_isEmpty(firstName) && !_isEmpty(lastName)) return "".concat(lastName);
  if (!_isEmpty(companyName)) return "".concat(companyName);
  return EMPTY_STRING;
};
export var getCustomerName = function getCustomerName(customer) {
  if (!_isEmpty(customer)) {
    var buyerTypeNum = _get(customer, 'buyerTypeNum');

    var buyerType = _isNil(buyerTypeNum) ? _get(customer, 'customerType') : buyerTypeNum;

    if (buyerType === CUSTOMER_TYPE.BUSINESS) {
      return _get(customer, 'legalName') || _get(customer, 'tradeName') || _get(customer, 'companyName') || getCustomerDisplayName(customer);
    }

    return getCustomerDisplayName(customer);
  }

  return EMPTY_STRING;
};
export var getCustomerCity = function getCustomerCity(address) {
  return _get(address, 'city', EMPTY_STRING) || EMPTY_STRING;
};
export var getCustomerState = function getCustomerState(address) {
  return _get(address, 'state', EMPTY_STRING) || EMPTY_STRING;
};
export var getCustomerPostalCode = function getCustomerPostalCode(address) {
  return _get(address, 'postalCode', EMPTY_STRING) || EMPTY_STRING;
};
export var getCustomerCounty = function getCustomerCounty(address) {
  return _get(address, 'county', EMPTY_STRING) || EMPTY_STRING;
};
export var getAddressLine1 = function getAddressLine1(address) {
  return _get(address, 'line1', EMPTY_STRING) || EMPTY_STRING;
};
export var getAddressLine2 = function getAddressLine2(address) {
  return _get(address, 'line2', EMPTY_STRING) || EMPTY_STRING;
};
export var getCustomerGmCardAccountNumber = function getCustomerGmCardAccountNumber(customer) {
  return _get(customer, 'gmCardNumber', EMPTY_STRING);
};
export var getCustomerType = function getCustomerType(_ref3) {
  var customerType = _ref3.customerType;
  return CUSTOMER_TYPE_DISPLAY[customerType] || NO_DATA;
};
export var getDOB = function getDOB(customer) {
  var dob = getBirthDate(customer);
  if (dob) return getFormattedDateTime(toMoment(dob), DATE_TIME_FORMATS.ABBREVIATED_DATE_ALPHA_MONTH);
  return NO_DATA;
};
export var getEmail = function getEmail(_ref4) {
  var email = _ref4.email;
  return _isEmpty(email) ? NO_DATA : email;
};

var getPhoneBasedOnType = function getPhoneBasedOnType(contact, type) {
  if (_isEmpty(contact)) return NO_DATA;
  var phoneObj = contact.find(function (phone) {
    return phone.type === type;
  });

  var phone = _get(phoneObj, 'number');

  return phone || NO_DATA;
};
/**
 * get either mobileNo, homeNo or workNo in the same priority
 */


export var getAnyPhoneNumberAsPerPriority = function getAnyPhoneNumberAsPerPriority(data) {
  var _ref5 = data || EMPTY_OBJECT,
      contact = _ref5.contact,
      mobileNo = _ref5.mobileNo,
      homePhone = _ref5.homePhone,
      workPhone = _ref5.workPhone;

  var mayBeBuildVehicle = _isEmpty(contact);

  var isValidPhoneNumber = function isValidPhoneNumber(val) {
    return !_isEmpty(val) && val !== NO_DATA;
  };

  var phoneNumber = mayBeBuildVehicle ? mobileNo : getMobile(data);
  if (isValidPhoneNumber(phoneNumber)) return phoneNumber;
  phoneNumber = mayBeBuildVehicle ? homePhone : getHomePhone(data);
  if (isValidPhoneNumber(phoneNumber)) return phoneNumber;
  phoneNumber = mayBeBuildVehicle ? workPhone : getWorkPhone(data);
  if (isValidPhoneNumber(phoneNumber)) return phoneNumber;
  return NO_DATA;
};
export var getHomePhone = function getHomePhone(_ref6) {
  var _ref6$contact = _ref6.contact,
      contact = _ref6$contact === void 0 ? EMPTY_ARRAY : _ref6$contact;
  return getPhoneBasedOnType(contact, DSE_CONTACT_TYPES.HOME);
};
export var getWorkPhone = function getWorkPhone(_ref7) {
  var _ref7$contact = _ref7.contact,
      contact = _ref7$contact === void 0 ? EMPTY_ARRAY : _ref7$contact;
  return getPhoneBasedOnType(contact, DSE_CONTACT_TYPES.WORK);
};
export var getMobile = function getMobile(_ref8) {
  var _ref8$contact = _ref8.contact,
      contact = _ref8$contact === void 0 ? EMPTY_ARRAY : _ref8$contact;
  return getPhoneBasedOnType(contact, DSE_CONTACT_TYPES.MOBILE);
};
export var getZIPCode = function getZIPCode(_ref9) {
  var data = _ref9.data;

  var addresses = _get(data, 'addresses');

  if (addresses) {
    var billingAddress = addresses.filter(function (address) {
      return address.addressType === 'BILLING_ADDRESS';
    });
    var shippingAddress = addresses.filter(function (address) {
      return address.addressType === 'SHIPPING_ADDRESS';
    });
    if (!_isEmpty(billingAddress)) return _get(_first(billingAddress), 'postalCode');
    if (!_isEmpty(shippingAddress)) return _get(_first(shippingAddress), 'postalCode');
  }

  return undefined;
};
export var getContactBasedOnTypes = function getContactBasedOnTypes(contacts, type) {
  return _get(_find(contacts, function (contact) {
    return contact.type === type;
  }), 'number');
};

var getSSN = function getSSN(customer) {
  return _get(customer, 'setup.sale.socialSecurityNumber');
};

var getBirthDate = function getBirthDate(customer) {
  return _get(customer, 'setup.sale.dateOfBirth');
};

export var getDOBObject = function getDOBObject(customer) {
  return _get(customer, 'setup.sale.dob');
};
export var getCustomerData = function getCustomerData(_ref10) {
  var customerId = _ref10.id,
      customerType = _ref10.customerType,
      firstName = _ref10.firstName,
      lastName = _ref10.lastName,
      middleName = _ref10.middleName,
      email = _ref10.email,
      legalName = _ref10.businessName,
      tradeName = _ref10.companyName,
      phones = _ref10.contact,
      birthDate = _ref10.dateOfBirth,
      externalCustomerId = _ref10.externalCustomerId,
      buyerTypeNum = _ref10.buyerTypeNum,
      suffix = _ref10.suffix,
      salutation = _ref10.salutation,
      displayId = _ref10.displayId,
      language = _ref10.language,
      rest = _objectWithoutProperties(_ref10, _excluded);

  return {
    customerId: customerId,
    customerType: _isNil(customerType) ? buyerTypeNum : customerType,
    suffix: suffix,
    salutation: salutation,
    firstName: firstName,
    lastName: lastName,
    middleName: middleName,
    email: email,
    legalName: legalName,
    tradeName: tradeName,
    mobileNo: getContactBasedOnTypes(phones, DSE_CONTACT_TYPES.MOBILE),
    homePhone: getContactBasedOnTypes(phones, DSE_CONTACT_TYPES.HOME),
    workPhone: getContactBasedOnTypes(phones, DSE_CONTACT_TYPES.WORK),
    birthDate: getBirthDate(rest),
    dob: getDOBObject(rest),
    ssn: getSSN(rest),
    externalCustomerId: externalCustomerId,
    customerIdDisplayValue: externalCustomerId || customerId,
    displayId: displayId,
    language: language
  };
};
export var getLicenseData = function getLicenseData(customer) {
  return {
    licenseNumber: _get(customer, 'setup.sale.licenseNumber'),
    expirationTime: _get(customer, 'setup.sale.dlExpiryDate'),
    issuingState: _get(customer, 'setup.sale.state'),
    issuingTime: _get(customer, 'setup.sale.dlIssuedDate')
  };
};
export var getInsuranceData = function getInsuranceData(customer) {
  var insurancePolicyInfo = _head(_get(customer, 'insurancePolicyInfos', [])) || {};
  var policyNumber = insurancePolicyInfo.policyNumber,
      insuranceCompany = insurancePolicyInfo.policyCompany,
      effectiveTime = insurancePolicyInfo.policyEffectiveDate,
      expirationTime = insurancePolicyInfo.policyExpiryDate,
      deductibleCollision = insurancePolicyInfo.deductibleCollision,
      deductibleComprehensive = insurancePolicyInfo.deductibleComprehensive,
      name = insurancePolicyInfo.agencyName,
      phoneNume = insurancePolicyInfo.phoneNumber,
      addressObj = insurancePolicyInfo.address,
      cmsInsuranceId = insurancePolicyInfo.id;

  var _ref11 = addressObj || {},
      address = _ref11.line1,
      zipCode = _ref11.postalCode,
      city = _ref11.city,
      country = _ref11.country,
      county = _ref11.county,
      state = _ref11.state;

  return {
    cmsInsuranceId: cmsInsuranceId,
    policyNumber: policyNumber,
    insuranceCompany: insuranceCompany,
    effectiveTime: effectiveTime,
    expirationTime: expirationTime,
    deductibleCollision: deductibleCollision,
    deductibleComprehensive: deductibleComprehensive,
    name: name,
    phoneNume: phoneNume,
    address: address,
    zipCode: zipCode,
    city: city,
    country: country,
    county: county,
    state: state
  };
};
export var getEmployerData = function getEmployerData(customer) {
  var employerData = _get(customer, 'employers');

  if (!_size(employerData)) return null;
  return _map(employerData, function (employer) {
    var employerName = employer.name,
        phoneNum = employer.phone,
        emailId = employer.email,
        occupation = employer.title,
        currentEmployer = employer.currentEmployer,
        address1 = employer.line1,
        zipCode = employer.postalCode,
        city = employer.city,
        state = employer.state,
        country = employer.country,
        countyName = employer.county,
        status = employer.employmentStatus,
        cmsEmployeeId = employer.id,
        employerType = employer.employerType,
        employmentType = employer.employmentType,
        periodOfEmployment = employer.periodOfEmployment,
        periodOfEmploymentDurationType = employer.periodOfEmploymentDurationType,
        grossIncome = employer.grossIncome,
        grossIncomeInterval = employer.grossIncomeInterval;
    var employmentStatus = currentEmployer ? EMPLOYER_TYPE.CURRENT : EMPLOYER_TYPE.PREVIOUS;
    return {
      cmsEmployeeId: cmsEmployeeId,
      employerName: employerName,
      phoneNum: phoneNum,
      emailId: emailId,
      occupation: occupation,
      currentEmployment: employmentStatus,
      address1: address1,
      zipCode: zipCode,
      city: city,
      state: state,
      country: country,
      countyName: countyName,
      status: status,
      employerType: employerType,
      employmentType: employmentType,
      periodOfEmployment: periodOfEmployment,
      periodOfEmploymentDurationType: periodOfEmploymentDurationType || DURATION_TYPES.YEAR,
      grossIncome: grossIncome,
      grossIncomeInterval: grossIncomeInterval
    };
  });
};
export var getContactPreferences = function getContactPreferences(customer) {
  var primaryContact = (_get(customer, 'contact') || EMPTY_ARRAY).find(function (customerInfo) {
    return customerInfo.type === DSE_CONTACT_TYPES.MOBILE;
  }) || EMPTY_OBJECT;
  var optOutFromMobile = _get(primaryContact, 'optOutOfMarketingCalls') || false;
  var optOutFromEmail = _get(customer, 'optOutOfMarketingEmails') || false;
  return {
    optOutFromEmail: optOutFromEmail,
    optOutFromMobile: optOutFromMobile
  };
}; // ==========================================================================================================
// Readers related to Customers from Deals (Customer Object in Deal)
// ==========================================================================================================

export var getCustomerNameWithSeparator = function getCustomerNameWithSeparator(customers) {
  if (_isEmpty(customers)) return '–';
  var customerNamesArray = customers.map(function (customer) {
    return "".concat(customer.firstName || '', " ").concat(customer.lastName || '');
  });
  return customerNamesArray.filter(function (customerName) {
    return customerName;
  }).join(' | ');
}; //  Specifically used for the update flow for Customer

export var getZipCodeOfExistingCustomer = function getZipCodeOfExistingCustomer(customers) {
  var buyer = _first(customers);

  if (_isEmpty(buyer)) return undefined;
  if (_get(buyer, 'newCustomer') || _get(buyer, 'existingCustomer', false)) return _get(buyer, 'address.zipCode');
  return getZIPCode({
    data: buyer
  });
};
export var getCustomerAddressDetails = function getCustomerAddressDetails(customer) {
  var address = getCustomerAddressObject(customer);
  return {
    address1: _get(address, 'address1') || EMPTY_STRING,
    city: _get(address, 'city') || EMPTY_STRING,
    state: _get(address, 'state') || EMPTY_STRING,
    country: _get(address, 'country') || EMPTY_STRING,
    zipCode: _get(address, 'zipCode') || EMPTY_STRING,
    countyName: _get(address, 'countyName') || EMPTY_STRING,
    marketId: _get(address, 'marketId') || EMPTY_STRING
  };
};
export var getCustomerAddressObject = function getCustomerAddressObject(customer) {
  var currentAddress = _filter(_get(customer, 'address'), function (address) {
    return address.addressType === ADDRESS_TYPE.CURRENT;
  });

  if (_isEmpty(currentAddress)) return EMPTY_OBJECT;
  return _first(currentAddress);
};
export var isTempCustomer = function isTempCustomer(customers) {
  return customers && !_isEmpty(customers.find(function (customer) {
    return customer.temp;
  }));
};
export var getBuyerInfo = function getBuyerInfo() {
  var customers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  if (_isEmpty(customers)) return EMPTY_OBJECT;
  return (customers || []).find(function (customer) {
    return customer.type === BUYER_TYPE.BUYER;
  }) || {};
};
export var getCoBuyerInfo = function getCoBuyerInfo() {
  var customers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  if (_isEmpty(customers)) return EMPTY_OBJECT;
  return (customers || []).find(function (customer) {
    return customer.type === BUYER_TYPE.CO_BUYER;
  }) || {};
};
export var getApplicantDetail = function getApplicantDetail() {
  var customers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  var applicantType = arguments.length > 1 ? arguments[1] : undefined;
  if (_isEmpty(customers)) return EMPTY_OBJECT;
  return (customers || []).find(function (customer) {
    return customer.type === applicantType;
  });
};
export var getBuyerName = function getBuyerName() {
  var customers = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  return getCustomerName(getBuyerInfo(customers));
};
export var getCustomerId = function getCustomerId() {
  var customer = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_OBJECT;
  return customer.customerId;
};
export var getAddressOfBuyerInDeal = function getAddressOfBuyerInDeal(buyer) {
  var addressType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : CUSTOMER_ADDRESS_TYPES.CURRENT;

  var addresses = _get(buyer, 'address');

  if (_isEmpty(addresses)) return EMPTY_OBJECT;
  var currentAddress = addresses.find(function (address) {
    return address.addressType === addressType;
  });
  return currentAddress || {};
};
export var getCurrentZIPCodeOfBuyerInDeal = function getCurrentZIPCodeOfBuyerInDeal(buyer) {
  var currentBuyerAddress = getAddressOfBuyerInDeal(buyer, CUSTOMER_ADDRESS_TYPES.CURRENT);

  var zipCode = _get(currentBuyerAddress, 'zipCode');

  return zipCode;
};
export var getCurrentMarketIdOfBuyerInDeal = function getCurrentMarketIdOfBuyerInDeal(buyer) {
  var currentBuyerAddress = getAddressOfBuyerInDeal(buyer, CUSTOMER_ADDRESS_TYPES.CURRENT);

  var marketId = _get(currentBuyerAddress, 'marketId');

  return marketId;
};

var getCustomerTypeFromCustomer = function getCustomerTypeFromCustomer(customer) {
  return _get(customer, 'customerType') || CUSTOMER_TYPE.PERSONAL;
};

var getAddressBasedOnType = function getAddressBasedOnType(addresses, type) {
  return _filter(addresses, function (address) {
    return address.addressType === type;
  });
};

var parseCustomerAddress = function parseCustomerAddress(addresses) {
  return _map(addresses, function (_ref12) {
    var residentType = _ref12.residentType,
        addressType = _ref12.currentAddress,
        address1 = _ref12.line1,
        address2 = _ref12.line2,
        periodOfResidence = _ref12.periodOfResidence,
        periodOfResidenceDurationType = _ref12.periodOfResidenceDurationType,
        mortageValue = _ref12.mortageValue,
        zipCode = _ref12.postalCode,
        city = _ref12.city,
        state = _ref12.state,
        country = _ref12.country,
        countyName = _ref12.county,
        cmsAddressId = _ref12.id;
    return {
      cmsAddressId: cmsAddressId,
      residentType: residentType,
      addressType: addressType ? CUSTOMER_ADDRESS_TYPES.CURRENT : CUSTOMER_ADDRESS_TYPES.PREVIOUS,
      address1: address1,
      address2: address2,
      periodOfResidence: periodOfResidence,
      periodOfResidenceDurationType: periodOfResidenceDurationType || DURATION_TYPES.YEAR,
      mortageValue: mortageValue,
      zipCode: zipCode,
      city: city,
      state: state,
      country: country,
      countyName: countyName
    };
  });
};

export var getAddressData = function getAddressData(customer, customerType) {
  var customerAddress = _get(customer, 'addresses');

  var addresses = [].concat(_toConsumableArray(getAddressBasedOnType(customerAddress, ADDRESS_TYPES.BILLING_ADDRESS)), _toConsumableArray(getAddressBasedOnType(customerAddress, ADDRESS_TYPES.SHIPPING_ADDRESS)));
  if (!_size(addresses)) addresses = _compact(_concat(addresses, customerAddress));
  if (customerType !== getCustomerTypeFromCustomer(customer) || !_size(addresses)) return null;

  var currentAddressFromCustomer = _find(addresses, 'currentAddress');

  var defaultAddresses = _filter(addresses, function (address) {
    return !address.currentAddress && address["default"];
  });

  var currentAddress = currentAddressFromCustomer || _head(defaultAddresses);

  var currentAddressWithFlag = currentAddress ? _objectSpread(_objectSpread({}, currentAddress), {}, {
    currentAddress: true
  }) : null;
  var previousAddress = currentAddressFromCustomer ? defaultAddresses[0] : defaultAddresses[1];
  return parseCustomerAddress(_compact(customerType === CUSTOMER_TYPE.PERSONAL ? [currentAddressWithFlag, previousAddress] : _castArray(currentAddressWithFlag)));
};
export var getContactNumber = function getContactNumber(customer) {
  var _ref13 = customer || EMPTY_OBJECT,
      mobileNo = _ref13.mobileNo,
      homePhone = _ref13.homePhone,
      workPhone = _ref13.workPhone;

  return mobileNo || homePhone || workPhone || '';
};
export var getCustomerContactList = function getCustomerContactList(customer) {
  var _ref14 = customer || EMPTY_OBJECT,
      mobileNo = _ref14.mobileNo,
      homePhone = _ref14.homePhone,
      workPhone = _ref14.workPhone;

  return _compact([_get(_find(_get(customer, 'contact.phones'), function (phone) {
    return phone.preferred;
  }), 'phoneNumber'), mobileNo, homePhone, workPhone]);
};
export var getContactDetails = function getContactDetails(_ref15) {
  var email = _ref15.email,
      customerInfo = _objectWithoutProperties(_ref15, _excluded2);

  return {
    mobilePhone: getContactNumber(customerInfo),
    email: email || ''
  };
};
export function getCustomerNumberFromSearch(customer) {
  var customerDisplayId = _get(customer, ['displayId']);

  var externalSourceID = _get(customer, ['externalSourceInfo', 0, 'externalSourceID']);

  return externalSourceID || customerDisplayId;
}
export function getCustomerNumberForDisplay(customer) {
  var customerDisplayId = _get(customer, ['displayId']);

  var externalCustomerId = _get(customer, ['externalCustomerId']);

  return externalCustomerId || customerDisplayId;
}
export var getCurrentEmploymentInfoOfBuyer = function getCurrentEmploymentInfoOfBuyer(customer) {
  var employmentDetails = _get(customer, 'employmentDetails') || [];
  return employmentDetails.find(function (_ref16) {
    var currentEmployment = _ref16.currentEmployment;
    return currentEmployment;
  }) || {};
};
export var getCurrentEmployment = function getCurrentEmployment(customer) {
  var employmentDetails = _get(customer, 'employmentDetails') || [];
  return _find(employmentDetails, ['currentEmployment', EMPLOYER_TYPE.CURRENT]);
};
export var getDateOfBirth = function getDateOfBirth(customer) {
  return _get(customer, 'birthDate');
};
export var getCurrentAddress = defaultMemoize(function () {
  var addresses = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : EMPTY_ARRAY;
  var address = _find(addresses, function (item) {
    return _get(item, 'default');
  }) || EMPTY_OBJECT;
  var city = getCustomerCity(address);
  var state = getCustomerState(address);
  var postalCode = getCustomerPostalCode(address);
  var county = getCustomerCounty(address);
  var line1 = getAddressLine1(address);
  var line2 = getAddressLine2(address);
  return {
    city: city,
    state: state,
    postalCode: postalCode,
    county: county,
    line1: line1,
    line2: line2
  };
});
export var getLeadId = function getLeadId(customer) {
  return _get(customer, 'leadId');
};
export var getMarketId = _property('marketId');
export var isCustomerTaxExempt = _property('customerTaxInfo.salesTaxInfo.0.taxExempted');